import React, { useEffect, useRef, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LayersIcon from '@mui/icons-material/Layers';
import { useQuery } from '@tanstack/react-query';
import { lessonService } from '../../services/lessonService';
import { imageService } from '../../services/imageService';
import { useSSEValidation } from '../../hooks/useSSEValidation';
import { useLessonStore } from '../../store/lessonStore';
import { useEditorStore } from '../../store/editorStore';
import { useAutoSave } from '../../hooks/useAutoSave';
import type { LessonPlan, LessonStage, SlideMapping, SlidesHTML } from '../../types';

const CEFR_COLORS: Record<string, string> = {
  A1: '#4caf50', A2: '#8bc34a',
  B1: '#2196f3', B2: '#1565c0',
  C1: '#9c27b0', C2: '#6a1b9a',
};

// Cycling accent colour per stage (index % palette length)
const STAGE_PALETTE = ['#5c6bc0', '#26a69a', '#ef5350', '#fb8c00', '#66bb6a', '#ab47bc', '#42a5f5'];

// Fallback prompt used only when there is no Phase 0 base_prompt
const FALLBACK_PROMPT = `You are an experienced online English language teacher and lesson planner.

CLASS CONTEXT:
- Platform: Online (Zoom / Teams), max 8 students, breakout rooms available
- Communicative, student-centred — 70%+ student talking time
- Clear stage aims, CCQs, minimal teacher talking time`;

// ─── Stage Card ───────────────────────────────────────────────────────────────

interface StageCardProps {
  stage: LessonStage;
  index: number;
  slideCount: number;
  readOnly: boolean;
  isRefining: boolean;
  onFieldChange: (field: keyof LessonStage, value: string | number) => void;
  onSlideChange: (delta: number) => void;
  onRefineOpen: (el: HTMLButtonElement) => void;
}

const StageCard: React.FC<StageCardProps> = ({
  stage, index, slideCount, readOnly, isRefining,
  onFieldChange, onSlideChange, onRefineOpen,
}) => {
  const [showSupport, setShowSupport] = useState(false);
  const color = STAGE_PALETTE[index % STAGE_PALETTE.length];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        position: 'relative',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,0.09)' },
      }}
    >
      {/* Left accent strip */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: color }} />

      {/* ── Header ────────────────────────────────────────────── */}
      <Box
        sx={{
          pl: 3.5, pr: 2, py: 1.5,
          bgcolor: '#fafafa',
          borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}
      >
        {/* Stage number badge */}
        <Box
          sx={{
            width: 30, height: 30, borderRadius: '50%',
            bgcolor: color, color: 'white', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800,
          }}
        >
          {index + 1}
        </Box>

        {/* Stage name */}
        <TextField
          value={stage.name}
          onChange={(e) => onFieldChange('name', e.target.value)}
          variant="standard"
          disabled={readOnly}
          slotProps={{ htmlInput: { style: { fontWeight: 700, fontSize: 15 } } }}
          sx={{ flex: 1, '& .MuiInput-underline:before': { borderBottomStyle: 'dashed' } }}
        />

        {/* Duration pill */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            bgcolor: 'action.hover', borderRadius: 10, px: 1.5, py: 0.4,
            border: '1px solid', borderColor: 'divider',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          <TextField
            value={stage.duration_min}
            onChange={(e) => onFieldChange('duration_min', Number(e.target.value))}
            type="number"
            variant="standard"
            disabled={readOnly}
            slotProps={{ htmlInput: { min: 1, style: { textAlign: 'right', width: 28, fontWeight: 600, fontSize: 13 } } }}
            sx={{ '& .MuiInput-root:before': { display: 'none' }, '& .MuiInput-root:after': { display: 'none' } }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>min</Typography>
        </Box>

        {/* Interaction chip */}
        {stage.interaction && (
          <Chip
            label={stage.interaction}
            size="small"
            sx={{
              fontSize: 11, maxWidth: 150, fontWeight: 600,
              border: `1px solid ${color}`, color, bgcolor: 'transparent',
            }}
          />
        )}

        {/* Refine button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={isRefining ? <CircularProgress size={12} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
          onClick={(e) => onRefineOpen(e.currentTarget)}
          disabled={readOnly || isRefining}
          sx={{
            whiteSpace: 'nowrap', flexShrink: 0, borderRadius: 2, fontSize: '0.72rem', fontWeight: 600,
            borderColor: color, color,
            '&:hover': { borderColor: color, bgcolor: `${color}12` },
          }}
        >
          Refine with AI
        </Button>
      </Box>

      {/* ── Body ──────────────────────────────────────────────── */}
      <Box sx={{ pl: 3.5, pr: 2, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Aim */}
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2, display: 'block', mb: 0.75 }}>
            Aim
          </Typography>
          <TextField
            value={stage.aim}
            onChange={(e) => onFieldChange('aim', e.target.value)}
            fullWidth size="small" multiline maxRows={2} disabled={readOnly}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        {/* Interaction pattern */}
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2, display: 'block', mb: 0.75 }}>
            Interaction Pattern
          </Typography>
          <TextField
            value={stage.interaction}
            onChange={(e) => onFieldChange('interaction', e.target.value)}
            fullWidth size="small" disabled={readOnly}
            placeholder="e.g. Pairs / Breakout rooms / Whole class"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        {/* Procedure */}
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2, display: 'block', mb: 0.75 }}>
            Procedure
          </Typography>
          <Box data-color-mode="light" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <MDEditor
              value={stage.procedure}
              onChange={(v) => onFieldChange('procedure', v ?? '')}
              height={220}
              preview="edit"
            />
          </Box>
        </Box>

        {/* Teaching support — collapsible */}
        <Box>
          <Button
            size="small"
            onClick={() => setShowSupport((s) => !s)}
            endIcon={showSupport ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, pl: 0, textTransform: 'uppercase' }}
          >
            Teaching Support
          </Button>
          <Collapse in={showSupport}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1.5 }}>
              <Box>
                <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2, display: 'block', mb: 0.75 }}>
                  Teacher Notes
                </Typography>
                <Box data-color-mode="light" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <MDEditor value={stage.teacher_notes ?? ''} onChange={(v) => onFieldChange('teacher_notes', v ?? '')} height={160} preview="edit" />
                </Box>
              </Box>
              <Box>
                <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2, display: 'block', mb: 0.75 }}>
                  Extension Activity
                </Typography>
                <Box data-color-mode="light" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <MDEditor value={stage.extension ?? ''} onChange={(v) => onFieldChange('extension', v ?? '')} height={130} preview="edit" />
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* ── Footer: slide stepper ─────────────────────────────── */}
      <Box
        sx={{
          pl: 3.5, pr: 2, py: 1.25,
          bgcolor: '#fafafa',
          borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <LayersIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, letterSpacing: 0.3 }}>
          Slides for this stage
        </Typography>
        <Box
          sx={{
            display: 'flex', alignItems: 'center',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 10, overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <IconButton size="small" onClick={() => onSlideChange(-1)} disabled={readOnly || slideCount <= 1}
            sx={{ borderRadius: 0, p: '4px 6px' }}>
            <RemoveIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography variant="body2" fontWeight={700} sx={{ minWidth: 28, textAlign: 'center', fontSize: 13, px: 0.5 }}>
            {slideCount}
          </Typography>
          <IconButton size="small" onClick={() => onSlideChange(1)} disabled={readOnly || slideCount >= 20}
            sx={{ borderRadius: 0, p: '4px 6px' }}>
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};


// ─── Phase 1 Planning ─────────────────────────────────────────────────────────

interface Phase1Props {
  lessonId: string;
  readOnly?: boolean;
}

export const Phase1Planning: React.FC<Phase1Props> = ({
  lessonId,
  readOnly = false,
}) => {
  const { currentLesson, currentVersion, updateLessonPlan, updateSlideMapping, updateSlidesHTML } = useLessonStore();
  const { setPhase, markDirty } = useEditorStore();
  const { generateLessonPlan, refineLessonPlan, isRunning } = useSSEValidation();

  const [customInstructions, setCustomInstructions] = useState('');
  const [proceedError, setProceedError] = useState('');
  const [generateError, setGenerateError] = useState(false);
  const [refineStage, setRefineStage] = useState<string | null>(null);
  const [refineMessage, setRefineMessage] = useState('');
  const [refineAnchorEl, setRefineAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [refineError, setRefineError] = useState(false);
  const [refineLoading, setRefineLoading] = useState(false);

  const lessonPlan = currentVersion?.lesson_plan ?? { stages: [] };
  const slideMapping = currentVersion?.slide_mapping ?? {};

  // A plan has real content when at least one stage has a procedure written.
  const hasContent = lessonPlan.stages.length > 0 &&
    lessonPlan.stages.some((s) => (s.procedure ?? '').trim().length > 0);
  // A skeleton has stage names/aims from Phase 0 but no generated procedures yet.
  const hasSkeletonOnly = lessonPlan.stages.length > 0 && !hasContent;

  const { data: lessonData } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => lessonService.getById(lessonId),
    enabled: !!lessonId,
    staleTime: 60_000,
  });

  const lesson = lessonData?.lesson ?? currentLesson;

  useEffect(() => {
    if (currentVersion) {
      setCustomInstructions(currentVersion.custom_instructions ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVersion?.id]);

  const saveData = {
    lesson_plan: lessonPlan,
    slide_mapping: slideMapping,
    custom_instructions: customInstructions,
    phase: 1 as const,
  };

  const hasUnsavedChanges = useEditorStore((s) => s.hasUnsavedChanges);
  useAutoSave({
    lessonId,
    version: currentVersion?.version_number ?? 1,
    data: saveData,
    isDirty: hasUnsavedChanges,
  });

  // Values always come from the store — no re-entry in Phase 1.
  const cefrLevel = lesson?.cefr_level ?? 'B1';
  const topic = lesson?.topic ?? '';
  const durationMin = lesson?.duration_min ?? 60;
  const basePrompt = currentVersion?.base_prompt ?? FALLBACK_PROMPT;

  // ── Generate ──
  const handleGenerate = async () => {
    setGenerateError(false);

    // When a Phase 0 skeleton exists, pass it so the AI fills in procedure/
    // teacher_notes/extension for the EXACT stages without renaming them.
    const stageSkeleton = lessonPlan.stages.length > 0
      ? lessonPlan.stages.map((s) => ({
          name: s.name,
          duration_min: s.duration_min,
          aim: s.aim ?? '',
          suggested_slides: s.suggested_slides ?? (slideMapping[s.name] ?? 2),
        }))
      : undefined;

    const result = await generateLessonPlan({
      lesson_id: lessonId,
      version: currentVersion?.version_number ?? 1,
      cefr_level: cefrLevel,
      topic,
      duration_min: durationMin,
      base_prompt: basePrompt,
      custom_instructions: customInstructions || undefined,
      stage_skeleton: stageSkeleton,
    });
    if (!result) {
      setGenerateError(true);
      return;
    }
    const versionNumber = currentVersion?.version_number ?? 1;
    const freshData = await lessonService.getById(lessonId);
    const plan = freshData?.version?.lesson_plan;
    if (plan) {
      updateLessonPlan(plan);
      // Preserve existing slide mapping; only fill gaps.
      const newMapping: SlideMapping = { ...slideMapping };
      plan.stages.forEach((s) => {
        if (!newMapping[s.name]) newMapping[s.name] = s.suggested_slides ?? 2;
      });
      updateSlideMapping(newMapping);
      markDirty();
    }

    // Clear downstream phases: the lesson plan changed so slides and images
    // generated from the old plan are now stale and must be regenerated.
    await Promise.all([
      lessonService.saveVersion(lessonId, currentVersion?.version_number ?? 1, {
        slides_html: {} as unknown as SlidesHTML,
        phase: 1,
      }),
      imageService.clearImages(lessonId, versionNumber),
    ]);
    updateSlidesHTML({} as unknown as SlidesHTML);
  };

  // Auto-generate as soon as the Phase 0 skeleton is available.
  const autoGenerateFiredRef = useRef(false);
  useEffect(() => {
    if (hasSkeletonOnly && !isRunning && !readOnly && !autoGenerateFiredRef.current) {
      autoGenerateFiredRef.current = true;
      void handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSkeletonOnly]);

  // ── Per-stage refine ──
  const handleRefineSubmitSSE = async () => {
    if (!refineMessage.trim()) return;
    setRefineError(false);
    setRefineLoading(true);
    const result = await refineLessonPlan({
      lesson_id: lessonId,
      version: currentVersion?.version_number ?? 1,
      message: refineMessage,
      current_plan: lessonPlan,
      stage_name: refineStage ?? undefined,
    });
    setRefineLoading(false);
    if (!result) {
      setRefineError(true);
      return;
    }
    const freshData = await lessonService.getById(lessonId);
    const plan = freshData?.version?.lesson_plan;
    if (plan) {
      updateLessonPlan(plan);
      markDirty();
    }
    setRefineStage(null);
    setRefineMessage('');
    setRefineAnchorEl(null);
  };

  // ── Stage edit handlers ──
  const handleStageFieldChange = (stageName: string, field: keyof LessonStage, value: string | number) => {
    const updatedPlan: LessonPlan = {
      stages: lessonPlan.stages.map((s) => s.name === stageName ? { ...s, [field]: value } : s),
    };
    updateLessonPlan(updatedPlan);
    markDirty();
  };

  const handleSlideChange = (stageName: string, delta: number) => {
    const current = slideMapping[stageName] ?? 2;
    updateSlideMapping({ ...slideMapping, [stageName]: Math.max(1, Math.min(20, current + delta)) });
    markDirty();
  };

  const handleRefineOpen = (stageName: string, el: HTMLButtonElement) => {
    setRefineStage(stageName);
    setRefineAnchorEl(el);
    setRefineMessage('');
  };

  const handleRefineClose = () => {
    setRefineStage(null);
    setRefineAnchorEl(null);
    setRefineMessage('');
  };

  const handleProceed = async () => {
    setProceedError('');
    if (lessonPlan.stages.length === 0) {
      setProceedError('Please generate a lesson plan before proceeding.');
      return;
    }
    await lessonService.saveVersion(lessonId, currentVersion?.version_number ?? 1, {
      ...saveData,
      phase: 2,
    });
    setPhase(2);
  };

  // ── Loading / error state before first content arrives ──────────────────────
  if (!hasContent) {
    return (
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          bgcolor: 'background.default',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {generateError ? (
          <>
            <Alert severity="error">
              Failed to generate lesson plan. Please check the quality panel and try again.
            </Alert>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => {
                autoGenerateFiredRef.current = false;
                void handleGenerate();
              }}
            >
              Retry
            </Button>
          </>
        ) : (
          <>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Generating lesson plan…
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // Derived stats for the sidebar summary
  const totalDuration = lessonPlan.stages.reduce((sum, s) => sum + (s.duration_min ?? 0), 0);
  const totalSlides   = lessonPlan.stages.reduce((sum, s) => sum + (slideMapping[s.name] ?? 2), 0);

  // ── Post-generation view ─────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100%' }}>

      {/* ── Left Sidebar ────────────────────────────────────────── */}
      <Box
        sx={{
          width: 264, flexShrink: 0,
          borderRight: '1px solid', borderColor: 'divider',
          position: 'sticky', top: 0, alignSelf: 'flex-start',
          maxHeight: '100vh', overflowY: 'auto',
          p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5,
          bgcolor: '#fafafa',
        }}
      >
        {/* Lesson overview */}
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 1 }}>
            Lesson
          </Typography>
          {topic && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.25, mb: 1, fontSize: '0.95rem', lineHeight: 1.35 }}>
              {topic}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={cefrLevel}
              size="small"
              sx={{ bgcolor: CEFR_COLORS[cefrLevel] ?? 'grey.500', color: 'white', fontWeight: 700, fontSize: '0.7rem' }}
            />
            <Chip label={`${durationMin} min`} size="small" variant="outlined" />
          </Box>
        </Box>

        {/* Stats summary */}
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2.5, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {[
            { label: 'Stages', value: lessonPlan.stages.length },
            { label: 'Total duration', value: `${totalDuration} min` },
            { label: 'Slides planned', value: totalSlides },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="caption" fontWeight={700}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Divider />

        {/* Custom instructions */}
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 1, display: 'block', mb: 1 }}>
            Custom Instructions
          </Typography>
          <TextField
            multiline rows={4} size="small" fullWidth
            value={customInstructions}
            onChange={(e) => { setCustomInstructions(e.target.value); markDirty(); }}
            disabled={readOnly}
            placeholder="Adjustments for the next regeneration…"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.83rem', bgcolor: 'background.paper' } }}
          />
        </Box>

        <Tooltip title={isRunning ? 'Generating…' : 'Regenerate the lesson plan using the instructions above'} placement="right">
          <span style={{ width: '100%' }}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
              onClick={handleGenerate}
              disabled={isRunning || readOnly}
              sx={{ borderRadius: 2, fontWeight: 600, fontSize: '0.82rem' }}
            >
              {isRunning ? 'Generating…' : 'Regenerate Plan'}
            </Button>
          </span>
        </Tooltip>

        {generateError && (
          <Alert severity="error" sx={{ fontSize: 12, borderRadius: 2 }}>Generation failed. Try again.</Alert>
        )}
      </Box>

      {/* ── Main Content ────────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0, bgcolor: 'background.default' }}>

        {/* Skeleton cards while (re)generating */}
        {isRunning &&
          Array.from({ length: 5 }).map((_, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#fafafa', px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton width="35%" height={22} />
                <Box sx={{ ml: 'auto' }}><Skeleton width={60} height={22} /></Box>
              </Box>
              <Box sx={{ px: 3.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Skeleton width="55%" height={18} />
                <Skeleton height={120} sx={{ borderRadius: 2 }} />
              </Box>
            </Paper>
          ))}

        {/* Stage cards */}
        {!isRunning &&
          lessonPlan.stages.map((stage, i) => (
            <StageCard
              key={stage.name}
              stage={stage}
              index={i}
              slideCount={slideMapping[stage.name] ?? 2}
              readOnly={readOnly}
              isRefining={refineLoading && refineStage === stage.name}
              onFieldChange={(field, value) => handleStageFieldChange(stage.name, field, value)}
              onSlideChange={(delta) => handleSlideChange(stage.name, delta)}
              onRefineOpen={(el) => handleRefineOpen(stage.name, el)}
            />
          ))}

        {/* Proceed footer */}
        {!isRunning && (
          <Box sx={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2,
            pt: 2, pb: 3,
          }}>
            {proceedError && <Alert severity="error" sx={{ borderRadius: 2 }}>{proceedError}</Alert>}
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleProceed}
              disabled={readOnly || lessonPlan.stages.length === 0}
              disableElevation
              sx={{ borderRadius: 2.5, px: 4, fontWeight: 700, fontSize: '0.9rem' }}
            >
              Proceed to Phase 2
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Per-stage Refine Popover ─────────────────────────────── */}
      <Popover
        open={Boolean(refineAnchorEl)}
        anchorEl={refineAnchorEl}
        onClose={handleRefineClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 4, sx: { width: 360, borderRadius: 3, p: 2.5 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Refine: <span style={{ fontWeight: 400, color: '#666' }}>{refineStage}</span>
          </Typography>
        </Box>
        <TextField
          multiline rows={3} fullWidth size="small" autoFocus
          value={refineMessage}
          onChange={(e) => setRefineMessage(e.target.value)}
          placeholder="What would you like to change about this stage?"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void handleRefineSubmitSSE(); }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, mb: 1.5 }}>
          Tip: ⌘ Enter to submit
        </Typography>
        {refineError && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12 }}>Refinement failed. Please try again.</Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleRefineClose} disabled={refineLoading} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            size="small" variant="contained" disableElevation
            onClick={() => void handleRefineSubmitSSE()}
            disabled={refineLoading || !refineMessage.trim()}
            startIcon={refineLoading ? <CircularProgress size={12} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Update Stage
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
